import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingClient, AccountingClientResetResult } from './accountingClientTypes'
import { resetAccountingClient } from './accountingClientsService'

interface ResetAccountingClientModalProps {
  officeUnitId: string
  client: AccountingClient | null
  isOpen: boolean
  onClose: () => void
  onReset: () => void
}

export function ResetAccountingClientModal({
  officeUnitId,
  client,
  isOpen,
  onClose,
  onReset,
}: ResetAccountingClientModalProps) {
  const { t } = useTranslation()
  const [confirmation, setConfirmation] = useState('')
  const [resetting, setResetting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AccountingClientResetResult | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setConfirmation('')
    setResetting(false)
    setError(null)
    setResult(null)
  }, [isOpen, client?.party_id])

  const confirmed = useMemo(() => (
    Boolean(client?.name) && confirmation.trim() === client?.name.trim()
  ), [client?.name, confirmation])

  const handleClose = () => {
    if (!resetting) onClose()
  }

  const handleReset = async () => {
    if (!client || !confirmed || resetting) return
    setResetting(true)
    setError(null)
    try {
      const completed = await resetAccountingClient(officeUnitId, client.client_unit_id)
      setResult(completed)
      onReset()
    } catch (resetError) {
      console.error('[ResetAccountingClientModal] Reset error:', resetError)
      setError(t('accounting.clients.reset.errors.run'))
    } finally {
      setResetting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('accounting.clients.reset.title')}
      size="sm"
      onSubmit={!result && confirmed ? handleReset : undefined}
      submitLabel={t('accounting.clients.reset.confirm')}
      submitting={resetting}
      closeLabel={t('actions.close')}
      showDefaultCloseButton={!resetting}
    >
      {result ? (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m5 13 4 4L19 7" /></svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('accounting.clients.reset.completed')}</h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('accounting.clients.reset.completedSummary', {
              documents: String(result.documents),
              movements: String(result.bookkeeping_movements),
              periods: String(result.memory_imports),
            })}</p>
          </div>
          <button type="button" onClick={handleClose} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">{t('actions.close')}</button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
            <p className="font-semibold">{t('accounting.clients.reset.warning')}</p>
            <p className="mt-2">{t('accounting.clients.reset.description', { client: client?.name || '' })}</p>
          </div>

          <ul className="space-y-2 rounded-xl border border-slate-200 p-4 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-300">
            {(['uploads', 'documents', 'bookkeeping', 'memory'] as const).map((item) => (
              <li key={item} className="flex gap-2"><span className="text-rose-500">×</span><span>{t(`accounting.clients.reset.items.${item}`)}</span></li>
            ))}
          </ul>

          <p className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.clients.reset.preserved')}</p>

          <label className="block">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('accounting.clients.reset.confirmationLabel', { client: client?.name || '' })}</span>
            <input
              type="text"
              value={confirmation}
              disabled={resetting}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
              className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            />
          </label>

          {!confirmed && confirmation.length > 0 && <p className="text-xs text-rose-600 dark:text-rose-300">{t('accounting.clients.reset.confirmationMismatch')}</p>}
          {resetting && <p className="text-sm font-medium text-rose-700 dark:text-rose-300">{t('accounting.clients.reset.running')}</p>}
          {error && <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>}
        </div>
      )}
    </Modal>
  )
}
